<template lang="pug">
#other-user-message-reply-detail
  // title
  .d-flex.flex-row.justify-content-center.mb-1(v-if="state.isSharedMessages == false")
    .page-title
      span {{ state.mainName }}（{{ state.subName }}）
  .mb-3.separator

  .card.mb-4
    // messages
    .lene-contents.d-flex.flex-column(v-BottomScroll)
      .mt-2(v-for="message in state.messages")
        // family message
        .line-left.mb-1(v-if="message.senderType == 1")
          .text.pre-line {{ message.message }}
          .datetime {{ getDisplayDate(message.datetime) }}
        // user message
        .line-right.mb-1(v-else)
          .text.pre-line {{ message.message }}
          .datetime(v-if="state.isSharedMessages == false") {{ getDisplayDate(message.datetime) }}
          .datetime(v-else) {{ message.groupMemberName }}・{{ message.groupMemberOrganization }} {{ getDisplayDate(message.datetime) }}
    // input reply message
    div.p-2
      ValidationAlert(v-model="showValidationAlert")
    .opinion-reply-contents.d-flex.flex-row.align-items-center
      .flex-grow-1.p-2
        b-form-textarea(v-model="replyMessage" name="reply-message" placeholder="返信内容を入力" rows="10" max-rows="10" maxlength="1000"
          v-validate="{ required: true, max: 1000, valid_link_url: true }" aria-describedby="text-live-feedback" data-vv-as="返信内容"
          @focus="hideFooter" @blur="showFooter" :disabled="isDeleted" :class="{ 'disable': isDeleted }")
      .reply-buttons.mr-2
        b-button.m-btn(@click="onReply" :disabled="isDeleted || !_useSendAndReply" :class="{ 'disable': isDeleted || !_useSendAndReply }") 送信
    .error-messages.pr-2.pb-2.pl-2(v-if="isDeleted == true")
      label.error-message.m-0 ユーザーが削除されているため返信できません

  // buttons
  .d-flex.flex-row.justify-content-center
    b-button.btn.back-btn(@click="onBack") 戻る

  // loading modal
  Loading(:isBusy="isBusy" :message="loadingMessage")
  // confirm reply modal
  OkCancelModal(v-model="showConfirmReply" title="以下のメッセージを送信しますか？" :message="replyConfirmMessage" @ok="onReplyModalOk")
  OkModal(v-model="showCompleteReply" title="メッセージ送信に成功しました。" @ok="onBack")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { userStore } from "@/store/store";
import { MobileMessageReplyDetailState } from "@/store/MobileMessageReplyDetail";
import { MobileUrl } from "@/commons/constants/MobileUrl";
import CustomValidate from "@/commons/utils/CustomValidate";

import OtherUserGroupMembersService from "@/commons/services/OtherUserGroupMembers";
import OtherUserGroupModel from "@/commons/models/OtherUserGroup";
import OtherUserGroupSendMessageRepliesService, { OtherUserGroupSendMessageRepliesPostModel } from "@/commons/services/OtherUserGroupSendMessageReplies";

import Loading from "@/mobile/basics/Loading.vue";
import ValidationAlert from "@/mobile/basics/ValidationAlertByValidator.vue";
import BottomScroll from "@/pc/basics/BottomScroll";
import OkCancelModal from "@/mobile/basics/OkCancelModal.vue";
import OkModal from "@/mobile/basics/OkModal.vue";

/**
 * ### 受信メッセージ詳細(家族)コンポーネント
 */
@Component({
  components: {
    Loading,
    ValidationAlert,
    OkCancelModal,
    OkModal
  },
  directives: {
    BottomScroll
  },
  $_veeValidate: { validator: "new" }
})
export default class OtherUserMessageReplyDetail extends Vue {
  /** メッセージ送信の返信サービス */
  @Inject("OtherUserGroupSendMessageRepliesService") otherUserGroupSendMessageRepliesService!: OtherUserGroupSendMessageRepliesService;
  /** 施設関係者グループメンバーサービス */
  @Inject("OtherUserGroupMembersService") otherUserGroupMembersService!: OtherUserGroupMembersService;

  /** 前画面で保持した情報 */
  @Getter("mobileMessageReplyDetailState") state!: MobileMessageReplyDetailState;

  /** 読み込み中モーダル表示可否 */
  isBusy: boolean = false;
  /** 読み込み中モーダルメッセージ */
  loadingMessage: string = "";

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 対象のユーザーが削除されているか否か */
  isDeleted: boolean = false;
  /** 返信メッセージ */
  replyMessage: string = "";
  /** 返信送信確認モーダル表示可否 */
  showConfirmReply: boolean = false;
  /** 返信送信確認モーダルメッセージ */
  replyConfirmMessage: string = "";
  /** 返信完了モーダル表示可否 */
  showCompleteReply: boolean = false;

  get _useSendAndReply(): boolean {
    return userStore.loginUser.permissions.sendMessagesFunction.useSendAndReply;
  }

  /**
   * ### vue lifecycle created
   */
  public created(): void {
    CustomValidate.addCustomValidate(this.$validator);
    return;
  }

  public async mounted(): Promise<void> {
    this.loadingMessage = "読込み中";
    this.isBusy = true;

    // 共有メッセージかどうかで分岐する
    if (this.state.isSharedMessages == false) {
      const member: OtherUserGroupModel | null = await this.getOtherUserGroupMember(this.state.groupId as number, this.state.memberId as number);
      this.isDeleted = member == null ? true : false;
    } else {
      let existMember: boolean = false;
      for (const receiverDetail of this.state.receiverDetails) {
        // 家族の存在チェック
        if (existMember == false) {
          const splitedSortKey: Array<string> = receiverDetail.sortKey.split("_");
          const member: OtherUserGroupModel | null = await this.getOtherUserGroupMember(Number(splitedSortKey[0]), Number(splitedSortKey[1]));
          if (member) {
            existMember = true;
            break;
          }
        }
      }
      this.isDeleted = !existMember;
    }

    this.isBusy = false;

    return;
  }

  /**
   * ### 施設関係者グループメンバーを取得する
   *
   * - 施設関係者グループメンバー単一を取得するAPIはないので、施設関係者グループにひもづくメンバーを取得する
   * - 施設関係者グループメンバーの中からメンバーIDが同一のものを修正対象のメンバーと判断する
   */
  public async getOtherUserGroupMember(groupId: number, memberId: number): Promise<OtherUserGroupModel | null> {
    const members: Array<OtherUserGroupModel> = await this.otherUserGroupMembersService.get(userStore, groupId);

    for (const member of members) {
      if (member.memberId == memberId) {
        return member;
      }
    }
    return null;
  }

  /**
   * ### 画面表示用の日付を返す
   */
  public getDisplayDate(sendTime: number): string {
    if (sendTime) {
      return this.$dateUtil.convertNumberToString(sendTime, "mm月dd日 hh:MM");
    } else {
      return "";
    }
  }

  /**
   * ### フッターを表示する
   */
  public showFooter(): void {
    this.$emit("updateShowFooter", true);
    return;
  }

  /**
   * ### フッターを非表示にする
   */
  public hideFooter(): void {
    this.$emit("updateShowFooter", false);
    return;
  }

  /**
   * ### 送信ボタン押下イベント
   *
   * - 返信内容を送信する
   */
  public async onReply(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.replyConfirmMessage = this.replyMessage;
      this.showConfirmReply = true;
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  /**
   * ### 返信送信確認モーダルOKイベント
   *
   * - 返信内容を意見メッセージとして登録する
   * - 登録したメッセージを追加して画面情報を更新する
   * - 返信内容はクリアする
   * - 返信成功ダイアログを表示する
   */
  public async onReplyModalOk(): Promise<void> {
    this.loadingMessage = "送信中";
    this.isBusy = true;

    const requestBody: OtherUserGroupSendMessageRepliesPostModel = {
      message: this.replyMessage
    };
    await this.otherUserGroupSendMessageRepliesService.post(userStore, this.state.parentMessagesSortKey, this.state.groupId as number, this.state.memberId as number, requestBody);

    this.replyMessage = "";
    this.showCompleteReply = true;

    this.isBusy = false;

    return;
  }

  /**
   * ### 戻るボタン押下イベント
   */
  public onBack(): void {
    const type: string = this.$route.params.type;
    this.$router.push({ name: MobileUrl.OtherUserMessageHistoryDetail, params: { type: type } });
    return;
  }
}
</script>

<style lang="scss" scopeed>
@import "../../../styles/mobile/_colors.scss";

#other-user-message-reply-detail {
  .lene-contents {
    overflow-y: auto;
    height: calc(30vh);
    background-color: $bg-message;

    .line-left,
    .line-right {
      position: relative;
      clear: both;

      .text {
        padding: 10px;
        border-radius: 20px;
        margin: 0;
        // LINEの吹き出しのように見せるために、CSSで図形を描画
        &::after {
          content: "";
          position: absolute;
          display: block;
          width: 0;
          height: 0;
          top: 10px;
          border-top: 10px solid transparent;
          border-bottom: 10px solid transparent;
        }
      }
      .datetime {
        color: $font-date;
        font-size: 0.9rem;
        font-weight: bold;
      }
    }

    // 家族の発言
    .line-left {
      display: inline-block;
      margin-bottom: 5px;
      margin-left: 30px;
      .text {
        background-color: $bg-message-bot;
        margin-right: 80px;
        &::after {
          left: -10px;
          border-right: 20px solid $bg-message-bot;
        }
      }
      .datetime {
        text-align: left;
      }
    }
    // 自分の発言
    .line-right {
      float: right;
      margin-top: 5px;
      margin-right: 30px;
      .text {
        background-color: $bg-message-mine;
        color: rgba(255, 255, 255, 1);
        margin-left: 80px;
        // LINEの吹き出しのように見せるために、CSSで図形を描画
        &::after {
          right: -10px;
          border-left: 20px solid $bg-message-mine;
        }
      }
      .datetime {
        text-align: right;
      }
    }
  }

  .reply-buttons {
    text-align: center;
    min-width: 54px;
  }

  .back-btn {
    min-width: 84px;
  }
}
</style>
